import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AssetsService } from '@sc/data-access-assets';
import { ConfigurationService } from '@sc/data-access-configuration';
import { Dossier, DossierCommentsEnum, lang } from '@sc/data-access-models';
import {
  DossierSandboxService,
  SandboxService,
} from '@sc/data-access-state-management';
import { TranslatorService } from '@sc/data-access-translation';
import { AdaptableOverlayService } from '@sc/feature-adaptable-overlay';
import { AuthService } from '@sc/feature-auth-okta';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import {
  LanguageDisplay,
  languageDisplayMapping,
} from '@sc/feature-language-selector';
import { ThemesManagerService } from '@sc/feature-themes-manager';
import {
  ButtonComponent,
  FlagComponent,
  IconComponent,
  InsurerLogoComponent,
} from '@sc/ui-components';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

@Component({
  selector: 'sc-insurer-navbar',
  styleUrls: ['./nav-bar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    InsurerLogoComponent,
    IconComponent,
    NgIf,
    ButtonComponent,
    NgClass,
    NgFor,
    MatTooltipModule,
    MatMenuModule,
    AsyncPipe,
    TranslateModule,
    FlagComponent,
  ],
  template: `
    <!-- mobile nav bar, hidden in desktop by CSS -->
    <div class="mobile-nav-bar">
      <sc-insurer-logo></sc-insurer-logo>
      <div class="menu-burger" (click)="toggleMobileNavBar()">
        <sc-icon name="menu" class="mobile-menu-toggle"></sc-icon>
      </div>
    </div>

    <div class="insurer-navbar-container" [class.active]="isMobileNavActive">
      <div class="leftSection">
        <sc-insurer-logo></sc-insurer-logo>
        <nav
          class="menu"
          role="navigation"
          [attr.aria-label]="
            'GlobalApplicativeConstants.accessibility.labels.navigation'
              | translate
          "
        >
          <ul class="navigation-list">
            <li>
              <sc-button
                [ariaRole]="isOnDashboardPage ? undefined : 'link'"
                [ngClass]="{ 'selected-page': isOnDashboardPage }"
                *ngIf="userAllowedToDashboardPage"
                (click)="goToDashboard()"
                >{{
                  'FeatureInsurerNavBar.NavBar.stats' | translate
                }}</sc-button
              >
            </li>
            <li>
              <sc-button
                [ariaRole]="isOnAdminPage ? undefined : 'link'"
                [ngClass]="{ 'selected-page': isOnAdminPage }"
                *ngIf="userAllowedToAdminPage"
                (click)="goToAdministration()"
                >{{
                  'FeatureInsurerNavBar.NavBar.administration' | translate
                }}</sc-button
              >
            </li>
            <li>
              <sc-button
                [ariaRole]="isOnPartnerFitterPage ? undefined : 'link'"
                [ngClass]="{ 'selected-page': isOnPartnerFitterPage }"
                *ngIf="userAllowedToPartnerFitterPage"
                (click)="goToPartners()"
                >{{
                  'FeatureInsurerNavBar.NavBar.partners' | translate
                }}</sc-button
              >
            </li>
            <li>
              <sc-button
                [ariaRole]="isOnDossiersSearchPage ? undefined : 'link'"
                [ngClass]="{ 'selected-page': isOnDossiersSearchPage }"
                *ngIf="userAllowedToDossiersSearchPage"
                (click)="goToDossiersSearch()"
                >{{
                  'FeatureInsurerNavBar.NavBar.dossiersSearch' | translate
                }}</sc-button
              >
            </li>
          </ul>
        </nav>
      </div>

      <span class="rightSection">
        <sc-button
          id="abandon-dossier-button"
          (click)="abandonDossier()"
          [style.visibility]="canAbandonDossier() ? 'visible' : 'hidden'"
          leftIcon="delete"
          buttonType="errorWhite"
          >{{
            'InsurerDeclarationContainer.abandonButtonTitle' | translate
          }}</sc-button
        >
        <sc-button
          type="button"
          [ariaRole]="isOnDeclarationPage ? 'button' : 'link'"
          *ngIf="
            featureFlippingManager.isFeatureActivated(
              'b2b-display-init-dossier-button'
            ) && (isInsurer$ | async)
          "
          buttonType="mainWhite"
          (click)="onInitDossier()"
          >{{
            'FeatureInsurerNavBar.NavBar.initDossier' | translate
          }}</sc-button
        >
        <div
          #langSelectPanel
          *ngIf="displayLangSelectPanel"
          class="language-select-panel"
          role="listbox"
          [attr.aria-label]="
            'GlobalApplicativeConstants.accessibility.labels.supportedLanguageList'
              | translate
          "
        >
          <div
            *ngFor="let supportedLang of supportedLangs"
            class="language-select-panel-option"
            role="option"
            tabindex="0"
            (click)="onSelectLang(supportedLang)"
            (keydown.enter)="onSelectLang(supportedLang)"
            (keydown.space)="onSelectLang(supportedLang)"
            (keydown.esc)="displayLangSelectPanel = false"
          >
            <sc-flag aria-hidden="true" [lang]="supportedLang.code"></sc-flag>
            <span class="country-label">{{ supportedLang.displayed }}</span>
          </div>
        </div>

        <span class="user_span">
          <sc-icon
            #tooltip="matTooltip"
            [matTooltip]="userNameAndInsurer$ | async"
            matTooltipPosition="right"
            name="user"
            aria-hidden="true"
          ></sc-icon>
          <span class="user_infos">
            <div class="logout">
              <button
                [matMenuTriggerFor]="menu"
                class="user-infos-menu-panel"
                [attr.aria-label]="
                  'GlobalApplicativeConstants.accessibility.labels.userOptions'
                    | translate: { userName: userName$ | async }
                "
              >
                {{ 'FeatureInsurerNavBar.NavBar.welcome' | translate }}
                {{ userName$ | async }}
                <sc-icon name="arrow_bottom" id="open-arrow" aria-hidden="true">
                </sc-icon>
              </button>
              <mat-menu #menu="matMenu" class="custom-navbar-mat-menu">
                <button mat-menu-item class="selected-user-info">
                  <sc-icon
                    #tooltip="matTooltip"
                    [matTooltip]="userName$ | async"
                    matTooltipPosition="right"
                    name="user"
                    id="user-information-icon"
                  ></sc-icon>
                  <span class="flex-grow-1"
                    >{{ 'FeatureInsurerNavBar.NavBar.welcome' | translate }}
                    {{ userName$ | async }}
                  </span>
                  <sc-icon name="arrow_top" id="close-arrow"></sc-icon>
                </button>
                <button
                  mat-menu-item
                  disabled
                  *ngIf="configurationService.getConfig().errorReportingUrl"
                  value="report-anomaly"
                  id="report-anomaly"
                  class="option-hover-color"
                  (click)="onSelectionChange('report-anomaly')"
                >
                  {{ 'FeatureInsurerNavBar.NavBar.reportError' | translate }}
                </button>
                <button
                  mat-menu-item
                  #changeLanguageOption
                  *ngIf="supportedLangs.length > 1"
                  value="change-language"
                  id="change-language"
                  class="option-hover-color"
                  [ngClass]="{
                    'selected-option-color': displayLangSelectPanel
                  }"
                  [attr.aria-label]="
                    'GlobalApplicativeConstants.accessibility.labels.languageSelection'
                      | translate
                        : { currentLang: currentLanguageCode?.displayed }
                  "
                  (click)="
                    trigger.openMenu();
                    $event.stopPropagation();
                    onSelectionChange('change-language')
                  "
                >
                  <sc-flag
                    [lang]="currentLanguageCode?.code"
                    aria-hidden="true"
                  ></sc-flag>
                  <span class="country-label">{{
                    currentLanguageCode?.displayed
                  }}</span>
                </button>
                <button
                  mat-menu-item
                  class="option-hover-color"
                  (click)="onSelectionChange('site-plan')"
                >
                  {{ 'FeatureFitterNavBar.NavBar.sitePlan' | translate }}
                </button>
                <button
                  (click)="onSelectionChange('user-logout')"
                  mat-menu-item
                  value="user-logout"
                  id="user-logout"
                >
                  <sc-icon name="logout" aria-hidden="true"></sc-icon
                  >{{ 'FeatureInsurerNavBar.NavBar.logout' | translate }}
                </button>
              </mat-menu>
            </div>
          </span>
        </span>
      </span>
    </div>
  `,
})
export class NavBarComponent implements OnInit, OnDestroy {
  @ViewChild('changeLanguageOption', { static: false, read: ElementRef })
  changeLanguageOption: ElementRef;

  @ViewChild(MatMenuTrigger) trigger: MatMenuTrigger;
  langSelectPanel: ElementRef<HTMLDivElement>;
  @ViewChild('langSelectPanel', { static: false }) set langSelectPanelSetter(
    langSelectRef: ElementRef<HTMLDivElement>
  ) {
    if (langSelectRef) {
      this.langSelectPanel = langSelectRef;

      (
        this.langSelectPanel.nativeElement.firstElementChild as HTMLDivElement
      ).focus();

      if (this.changeLanguageOption) {
        const posOptionY =
          this.changeLanguageOption.nativeElement.getBoundingClientRect().y +
          window.scrollY;
        this.langSelectPanel.nativeElement.style.top = `${posOptionY}px`;
      }
    }
  }

  @Output() initDossierButtonClickEvent = new EventEmitter();
  @Input() currentDossier: Dossier;
  @Input() userAllowedToAdminPage = false;
  @Input() userAllowedToDashboardPage = false;
  @Input() userAllowedToPartnerFitterPage = false;
  @Input() userAllowedToDossiersSearchPage = false;
  isOnPartnerFitterPage = false;
  isOnDashboardPage = false;
  isOnAdminPage = false;
  isOnDossiersSearchPage = false;
  isOnDeclarationPage = false;
  isMobileNavActive = false; // toggle for open and close mobile menu

  userName$: Observable<string>;
  userNameAndInsurer$: Observable<string>;

  isInsurer$: Observable<boolean>;

  displayLangSelectPanel = false;

  dropdownForm = new FormControl();
  languageForm = new FormControl();

  // The code of the currently selected language
  currentLanguageCode: LanguageDisplay = {
    code: 'fr_FR',
    displayed: 'Français',
  };
  // The list of supported languages for this configuration
  supportedLangs: LanguageDisplay[] = [];

  constructor(
    public assetsService: AssetsService,
    public router: Router,
    public themesManager: ThemesManagerService,
    private readonly authService: AuthService,
    public configurationService: ConfigurationService,
    private readonly translatorService: TranslatorService,
    public featureFlippingManager: FeatureFlippingManagerService,
    public dossierSB: DossierSandboxService,
    public sandboxService: SandboxService,
    private readonly overlayDialogService: AdaptableOverlayService
  ) {}

  ngOnInit() {
    this.isOnAdminPage =
      this.router?.routerState?.snapshot?.url === '/administration';
    this.isOnDashboardPage =
      this.router?.routerState?.snapshot?.url === '/dashboard';
    this.isOnPartnerFitterPage =
      this.router?.routerState?.snapshot?.url === '/partners';
    this.isOnDossiersSearchPage =
      this.router?.routerState?.snapshot?.url === '/dossiers-search';
    this.isOnDeclarationPage =
      this.router?.routerState?.snapshot?.url === '/declaration/sinister-data';

    this.userName$ = combineLatest([
      this.authService.getUserFirstName$(),
      this.authService.getUserLastName$(),
    ]).pipe(map(([firstName, lastName]) => `${firstName} ${lastName}`));
    this.userNameAndInsurer$ = this.userName$.pipe(
      map(
        (userName) =>
          `${userName} - ${
            this.configurationService?.getConfig()?.insurerName || ''
          }`
      )
    );

    this.isInsurer$ = this.authService.userIsInsurer$();

    this.dropdownForm.patchValue('user-information');

    // Take every supported language, keep only the ones in the current config and format them to have their code and their name
    // Then sort the languages in alphabetical order
    this.supportedLangs = Object.keys(languageDisplayMapping)
      .map((key: lang) => ({
        displayed: languageDisplayMapping[key],
        code: key,
      }))
      .filter((language) =>
        this.configurationService
          .getConfig()
          .supportedLang.includes(language.code)
      )
      .sort((a, b) => a.displayed.localeCompare(b.displayed));

    const storageLang = this.dossierSB.getDossierLanguage();
    let obsLang = of(null);
    if (storageLang) {
      obsLang = this.translatorService.use(storageLang);
    }

    obsLang.pipe(take(1)).subscribe(() => {
      const defaultLangCode: lang =
        this.translatorService.getCurrentLang().getValue() ||
        this.dossierSB.getDossierLanguage() ||
        this.configurationService.getConfig().defaultLang;
      this.currentLanguageCode = this.supportedLangs.find(
        (l) => l.code === defaultLangCode
      );
    });
  }

  @HostListener('window:beforeunload')
  unloadHandler() {
    this.sandboxService.unsubscribe();
  }

  onInitDossier() {
    this.initDossierButtonClickEvent.emit();
  }

  goToDashboard() {
    this.router.navigate(['/dashboard']).catch((error) => console.error(error));
  }

  goToAdministration() {
    this.router
      .navigate(['/administration'])
      .catch((error) => console.error(error));
  }

  ngOnDestroy(): void {
    this.initDossierButtonClickEvent?.complete();
  }

  goToPartners() {
    this.router.navigate(['/partners']).catch((error) => console.error(error));
  }

  goToDossiersSearch() {
    this.router
      .navigate(['/dossiers-search'])
      .catch((error) => console.error(error));
  }

  toggleMobileNavBar() {
    this.isMobileNavActive = !this.isMobileNavActive;
  }

  onSelectionChange(selectionEvent) {
    if (selectionEvent === 'user-logout') {
      this.authService.logout();
      this.router.navigate(['/login']).catch((error) => console.error(error));
    } else if (selectionEvent === 'report-anomaly') {
      window.open(
        this.configurationService.getConfig().errorReportingUrl ||
          'https://aari-glassdrive.zendesk.com/',
        '__blank'
      );
    } else if (selectionEvent === 'change-language') {
      this.displayLangSelectPanel = true;
    } else if (selectionEvent === 'site-plan') {
      const previousUrl = this.router.url;
      this.router.navigate(['/site-plan'], {
        queryParams: { previousUrl },
      });
    }

    this.dropdownForm.patchValue('user-information');
  }

  onSelectLang(langDisplay: LanguageDisplay) {
    this.translatorService.use(langDisplay.code);
    this.dossierSB.setDossierLanguage(langDisplay.code);
    this.currentLanguageCode = langDisplay;
    this.displayLangSelectPanel = false;
    this.trigger.closeMenu();
  }

  @HostListener('document:click', ['$event'])
  onClickDocument(event: MouseEvent) {
    if (event.type === 'click') {
      if (this.langSelectPanel) {
        if (
          this.displayLangSelectPanel &&
          !this.langSelectPanel.nativeElement.contains(event.target as Node)
        ) {
          this.displayLangSelectPanel = false;
        }
      }
    }
  }
  abandonDossier() {
    let currentRoute = this.router.url;
    this.overlayDialogService
      .showOverlay({ type: 'dossier-abandon', input: null })
      .subscribe((result) => {
        let onAbandon$ = of(false);
        if (result?.type === 'success') {
          onAbandon$ = this.dossierSB.dossier$.pipe(
            take(1),
            switchMap((dossier) =>
              this.dossierSB
                .abandonDossier({
                  id: dossier.id,
                  comment: result.comment,
                })
                .pipe(
                  switchMap((result) =>
                    this.dossierSB.patchDossier({
                      ...result.dossier,
                    })
                  )
                )
            ),
            map(() => {
              currentRoute = '/declaration/declaration-end';
              return false;
            })
          );
        } else if (result?.type === 'later') {
          onAbandon$ = this.dossierSB.dossier$.pipe(
            take(1),
            switchMap((dossier) =>
              this.dossierSB.patchDossier({
                state: dossier.state,
                dossierComments: [
                  ...(dossier.dossierComments || []),
                  {
                    userName:
                      `${this.authService.getAccountLastName()} ${this.authService.getAccountFirstName()}`.trim(),
                    userType: 'INSURER',
                    comment: result.comment,
                    timestamp: new Date().toISOString(),
                    commentType: DossierCommentsEnum.finishLater,
                    isPinned: false,
                  },
                ],
              })
            ),
            switchMap(() => this.dossierSB.updateDossier()),
            switchMap((dossier) => this.dossierSB.setDossier(dossier)),
            map(() => true)
          );
        }

        onAbandon$.subscribe((shouldInitNewDossier) => {
          if (shouldInitNewDossier) {
            this.onInitDossier();
          } else {
            this.router
              .navigate([currentRoute])
              .catch((error) => console.error(error));
          }
        });
      });
  }

  canAbandonDossier() {
    return (
      this.currentDossier?.id &&
      this.dossierSB.statesComplete.findIndex(
        (state) => state === this.currentDossier?.state
      ) === -1 &&
      this.currentDossier?.state !== 'ABANDONED' &&
      this.featureFlippingManager.isFeatureActivated('b2b-allow-abandon-files')
    );
  }
}
